import Stack from '@mui/material/Stack'
import { Grid, styled, Typography, Paper, Box, Table, TableHead, TableBody, TableRow, TableCell } from '@mui/material'
import { useAppTranslation } from 'src/admin/core/hooks'
import { formatAsCurrency } from 'src/admin/core'
import { formatToShortLocalDate, formatToShortLocalDateTime } from 'src/admin/core/services/dateFunctions'
import { TransactionData } from '..'
import { Enterprise, Agency } from 'src/core/models'
import { Workstation } from 'src/admin/scenes/entities/stations/models'
import { getAppBarState } from 'src/store'
import React, { useRef, forwardRef, useImperativeHandle } from 'react'
import jsPDF from 'jspdf'

const Label = styled(Grid)(({ theme }) => ({
  fontWeight: 'bold',
}))

interface PrintReportMetadata {
  productObj?: { value: string; label: string }
  enterpriseObj?: Enterprise
  agencyObj?: Agency
  workstationObj?: Workstation
  providerObj?: { value: string; label: string }
}

interface PrintReportProps {
  data: TransactionData[]
  fromDate: string
  toDate: string
  provider: string
  enterpriseName?: string
  hideHipodromo?: boolean
  metadata?: PrintReportMetadata
}

export const PrintReport = forwardRef(({ data, fromDate, toDate, provider, enterpriseName, hideHipodromo = false, metadata }: PrintReportProps, ref) => {
  const { label } = useAppTranslation()
  const {
    user: { profile },
  } = getAppBarState().appState
  const firstRecord = data.length > 0 ? data[0] : null
  const now = new Date()

  // Get info from metadata or fallback to first record
  const reportInfo = {
    enterpriseName: metadata?.enterpriseObj?.name || enterpriseName || '',
    agencyName: metadata?.agencyObj?.name || '',
    workstationName: metadata?.workstationObj?.name || data[0]?.workstationId || '',
    productName: label(metadata?.productObj?.label) || '',
    providerName: metadata?.providerObj?.label || provider || ''
  }

  // Ref for table
  const tableRef = useRef<HTMLDivElement>(null)

  // Expose the ref through a method that can be accessed from the parent component
  useImperativeHandle(ref, () => ({
    getTableRef: () => tableRef,
    // We no longer need footerRef since we've integrated it into the table
    downloadReport: () => {
      if (tableRef.current) {
        downloadReportAsPDF(
          tableRef,
          null, // Pass null for footerRef since we don't use it anymore
          'workstation-history',
          fromDate,
          toDate,
          provider,
          enterpriseName || '',
          data,
          label, // Pass the label function
          metadata, // Pass metadata
          hideHipodromo // Pass hideHipodromo flag
        )
      }
    }
  }));

  return (
    <Box sx={{ padding: 2 }}>
      {data.length > 0 && (
        <Stack direction="row" spacing={0} style={{ padding: '10px' }}>
          <Grid container spacing={0}>
            <Label item xs={12} md={12} textAlign="center" style={{ display: 'grid', marginBottom: '2px' }}>
              <span>{`${reportInfo.enterpriseName} | ${reportInfo.agencyName}${reportInfo.productName ? ' - ' + reportInfo.productName : ''}`}</span>
            </Label>
            <Label item xs={12} md={12} textAlign="center" style={{ display: 'grid', marginBottom: '2px' }}>
              <span>{`Historial por Estación ${reportInfo.workstationName}`}</span>
            </Label>
            <Label item xs={12} md={12} textAlign="center" style={{ display: 'grid', marginBottom: '4px' }}>
              <Typography variant="body1" textAlign="center" style={{ fontSize: 12 }}>
                {`Día ${formatToShortLocalDate(fromDate)}`}
              </Typography>
            </Label>                                    
          </Grid>
        </Stack>
      )}

      <Paper elevation={0} sx={{ mt: 1, mb: 0 }}>
        {/* Table view without footer for PDF export */}
        <div ref={tableRef} data-ref="tableRef">
          <Table size="small" sx={{ mb: 2 }}>
            <TableHead>
              <TableRow sx={{ backgroundColor: (theme) => theme.palette.grey[200] }}>
                <TableCell sx={{ padding: '2px 4px' }}>Hora</TableCell>
                <TableCell sx={{ padding: '2px 4px' }}>Tipo</TableCell>
                <TableCell align="right" sx={{ padding: '2px 4px' }}>Monto</TableCell>
                <TableCell align="right" sx={{ padding: '2px 4px' }}>Balance</TableCell>
                <TableCell sx={{ padding: '2px 4px' }}>Ticket</TableCell>
                {!hideHipodromo && <TableCell sx={{ padding: '2px 4px' }}>Hipódromo</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {data.map((row, index) => (
                <TableRow key={index}>
                  <TableCell sx={{ padding: '2px 4px' }}>{row.time}</TableCell>
                  <TableCell sx={{ padding: '2px 4px' }}>{label(row.type)}</TableCell>
                  <TableCell sx={{ textAlign: 'right', padding: '2px 4px' }}>{formatAsCurrency(row.amount, 2)}</TableCell>
                  <TableCell sx={{ textAlign: 'right', padding: '2px 4px' }}>{formatAsCurrency(row.balance, 2)}</TableCell>
                  <TableCell sx={{ padding: '2px 4px' }}>
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <span>{hideHipodromo ? row.lotteryTicketNumber : row.ticket}</span>
                      {!hideHipodromo && row.wagers && row.wagers.split('\n').map((wager: string, idx: number) => (
                        <span key={idx} style={{ fontSize: '0.85em', color: '#666' }}>{wager}</span>
                      ))}
                    </div>
                  </TableCell>
                  {!hideHipodromo && <TableCell sx={{ padding: '2px 4px' }}>{row.league}</TableCell>}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Paper>

      <Grid item xs={12} md={12} textAlign="right" style={{ marginTop: 10 }}>
        <Typography variant="body1" >
          {`${profile?.name || 'Usuario'} ${formatToShortLocalDateTime(now, 'DD-MM-YYYY h:mm:ss a')}`}
        </Typography>
      </Grid>
    </Box>
  )
})

// Function to download the report as PDF
export const downloadReportAsPDF = async (
  tableRef: React.RefObject<HTMLElement>,
  footerRef?: React.RefObject<HTMLElement> | null,
  filename: string = 'balance-estacion',
  fromDateStr: string = '0000-00-00',
  toDateStr: string = '0000-00-00',
  provider: string = '',
  enterpriseName: string = '',
  transactionData?: TransactionData[],
  labelFunction?: (key: string) => string,
  metadata?: PrintReportMetadata,
  hideHipodromo: boolean = false
) => {
  try {
    // Get info from metadata
    const reportInfo = {
      enterpriseName: metadata?.enterpriseObj?.name || enterpriseName || '',
      agencyName: metadata?.agencyObj?.name || '',
      workstationName: metadata?.workstationObj?.name || transactionData?.[0]?.workstationId || '',
      productName: labelFunction(metadata?.productObj?.label) || '',
      providerName: metadata?.providerObj?.label || provider || ''
    }

    // Create new PDF
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'letter',
    });
    
    // Page dimensions
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    
    // Margins
    const margins = {
      top: 20,
      bottom: 20,
      left: 15,
      right: 15,
    };
    
    // Get table elements
    const tableElement = tableRef.current;
    
    if (!tableElement || !transactionData || transactionData.length === 0) {
      throw new Error('Could not find necessary elements in the table or transaction data');
    }
    
    // Get header from the first table
    const firstTable = tableElement.querySelector('table');
    const headerRow = firstTable?.querySelector('thead tr');
    
    if (!headerRow) {
      throw new Error('Could not find table header');
    }
    
    // Table configuration
    const fontSize = 8; // Reduced from 9 to 8 for more compact text
    const lineHeight = fontSize * 0.55; // Reduced for more compact rows
    const rowsPerPage = Math.floor((pageHeight - margins.top - margins.bottom) / lineHeight);
    
    // Set font
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(fontSize);
    
    // Get column headers
    const headers = Array.from(headerRow.querySelectorAll('th')).map(th => th.textContent || '');
    
    // Calculate the maximum width needed for the Ticket column (index 4)
    let maxTicketWidth = 0;
    transactionData.forEach(row => {
      const ticketText = `${row.ticket}\n${row.wagers}`;
      const textWidth = pdf.getTextWidth(ticketText);
      maxTicketWidth = Math.max(maxTicketWidth, textWidth);
    });
    
    // Calculate width for each column
    const columnWidths = [];
    const tableWidth = pageWidth - margins.left - margins.right;
    const numColumns = headers.length;
    
    // Fixed widths for specific columns
    const fixedWidths: Record<number, number> = {
      0: tableWidth * 0.15, // Hora
      1: tableWidth * 0.08, // Tipo
      2: tableWidth * 0.12, // Monto
      3: tableWidth * 0.12, // Balance
    };
    
    // Only add Hipódromo width if not hidden
    if (!hideHipodromo) {
      fixedWidths[5] = tableWidth * 0.20; // Hipódromo (minimum)
    }
    
    // Calculate dynamic width for Ticket column (index 4)
    const fixedWidthsSum = Object.values(fixedWidths).reduce((sum, w) => sum + w, 0);
    const ticketWidth = hideHipodromo 
      ? tableWidth - fixedWidths[0] - fixedWidths[1] - fixedWidths[2] - fixedWidths[3]
      : Math.min(Math.max(maxTicketWidth + 5, tableWidth * 0.25), tableWidth - fixedWidthsSum);
    
    // Adjust Hipódromo width based on remaining space (only if not hidden)
    const hipodromoWidth = hideHipodromo 
      ? 0 
      : tableWidth - fixedWidths[0] - fixedWidths[1] - fixedWidths[2] - fixedWidths[3] - ticketWidth;
    
    for (let i = 0; i < numColumns; i++) {
      if (i === 4) {
        columnWidths.push(ticketWidth);
      } else if (i === 5 && !hideHipodromo) {
        columnWidths.push(hipodromoWidth);
      } else if (i !== 5) {
        columnWidths.push(fixedWidths[i]);
      }
    }
    
    // Function to draw a row
    const drawRow = (rowData, y, isHeader = false, isFooter = false) => {
      // Set style based on row type
      if (isHeader) {
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(fontSize);
        pdf.setFillColor(240, 240, 240); // Light gray for header
        pdf.rect(margins.left, y - lineHeight + 1, tableWidth, lineHeight + 2, 'F'); // Increased header height with more padding
      } else if (isFooter) {
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(fontSize);
      } else {
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(fontSize);
      }
      
      // Calculate max lines in this row for proper height
      let maxLines = 1;
      rowData.forEach((cellContent) => {
        if (cellContent && cellContent.includes('\n')) {
          const lines = cellContent.split('\n').length;
          maxLines = Math.max(maxLines, lines);
        }
      });
      
      // Calculate actual row height based on line spacing
      // Use full lineHeight for each line to prevent overlap
      // Add extra padding for header
      const rowHeight = isHeader ? lineHeight * maxLines + 2 : lineHeight * maxLines;
      
      // Draw each cell in the row
      let currentX = margins.left;
      for (let i = 0; i < rowData.length; i++) {
        const cellContent = rowData[i] || '';
        const cellWidth = columnWidths[i];
        const cellX = currentX;
        
        // Check if cell has multiple lines
        if (cellContent.includes('\n')) {
          const lines = cellContent.split('\n');
          let currentY = y;
          for (let lineIndex = 0; lineIndex < lines.length; lineIndex++) {
            let line = lines[lineIndex];
            const isRightAligned = i === 2 || i === 3;
            
            // For ticket column (index 4), wrap long lines at slashes
            if (i === 4 && lineIndex > 0) {
              const maxWidth = cellWidth - 4; // Account for padding
              const textWidth = pdf.getTextWidth(line);
              
              if (textWidth > maxWidth) {
                // Split by / and add line breaks as needed
                const groups = line.split('/');
                let currentLine = '';
                const wrappedLines: string[] = [];
                
                for (let groupIdx = 0; groupIdx < groups.length; groupIdx++) {
                  const group = groups[groupIdx];
                  const testLine = currentLine ? currentLine + '/' + group : group;
                  const testWidth = pdf.getTextWidth(testLine);
                  
                  if (testWidth > maxWidth && currentLine) {
                    wrappedLines.push(currentLine + '/');
                    currentLine = group;
                  } else {
                    currentLine = testLine;
                  }
                }
                
                if (currentLine) {
                  wrappedLines.push(currentLine);
                }
                
                // Draw each wrapped line
                for (let wrapIdx = 0; wrapIdx < wrappedLines.length; wrapIdx++) {
                  const wrappedLine = wrappedLines[wrapIdx];
                  if (lineIndex > 0) {
                    pdf.setFontSize(fontSize - 1);
                  }
                  pdf.text(wrappedLine, cellX + 2, currentY);
                  if (lineIndex > 0) {
                    pdf.setFontSize(fontSize);
                  }
                  if (wrapIdx < wrappedLines.length - 1) {
                    currentY += lineHeight;
                  }
                }
                currentY += lineHeight;
                continue;
              }
            }
            
            if (isRightAligned) {
              pdf.text(line, cellX + cellWidth - 2, currentY, { align: 'right' });
            } else {
              // Reduce font size for wager lines (not the first line)
              if (lineIndex > 0) {
                pdf.setFontSize(fontSize - 1);
              }
              pdf.text(line, cellX + 2, currentY);
              if (lineIndex > 0) {
                pdf.setFontSize(fontSize);
              }
            }
            currentY += lineHeight;
          }
        } else {
          // Single line cell
          if (i === 2 || i === 3) {
            pdf.text(cellContent, cellX + cellWidth - 2, y, { align: 'right' });
          } else {
            pdf.text(cellContent, cellX + 2, y);
          }
        }
        
        currentX += cellWidth;
      }
      
      // Restore normal font after header or footer
      if (isHeader || isFooter) {
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(fontSize);
      }
      
      return y + rowHeight;
    };
    
    // Prepare data for PDF generation
    const pdfRows: string[][] = [];
    
    transactionData.forEach(row => {
      // For lottery ticket, show lotteryTicketNumber; otherwise combine ticket and wagers
      const ticketInfo = hideHipodromo 
        ? String(row.lotteryTicketNumber || '')
        : (row.ticket ? (row.wagers ? `${row.ticket}\n${row.wagers}` : row.ticket) : '');
      
      const rowData = [
        String(row.time || ''),
        labelFunction ? labelFunction(row.type) : String(row.type || ''),
        formatAsCurrency(row.amount, 2),
        formatAsCurrency(row.balance, 2),
        ticketInfo,
      ];
      
      // Only add league column if not hidden
      if (!hideHipodromo) {
        rowData.push(String(row.league || ''));
      }
      
      pdfRows.push(rowData);
    });
    
    // Draw rows dynamically based on available space
    let currentRowIndex = 0;
    let pageNum = 0;
    
    while (currentRowIndex < pdfRows.length) {
      // Add new page if not the first
      if (pageNum > 0) {
        pdf.addPage();
      }
      
      let y = margins.top;
      
      // Draw title and dates if it's the first page
      if (pageNum === 0) {
        // Title
        pdf.setFontSize(fontSize + 2);
        pdf.setFont('helvetica', 'bold');
        const titleParts = [reportInfo.enterpriseName, reportInfo.agencyName, reportInfo.productName].filter(Boolean);
        pdf.text(titleParts.slice(0, 2).join(' | ') + (titleParts[2] ? ' - ' + titleParts[2] : ''), pageWidth / 2, y + lineHeight, { align: 'center' });
        y += lineHeight * 1.0;
        
        pdf.text(`Historial por Estación ${reportInfo.workstationName}`, pageWidth / 2, y + lineHeight, { align: 'center' });
        y += lineHeight * 1.2;
        
        // Product and date
        pdf.text(`Día ${formatToShortLocalDate(fromDateStr)}`, pageWidth / 2, y + lineHeight, { align: 'center' });
        y += lineHeight * 2.5; // More space after dates before table
      } else {
        y += lineHeight; // Additional space in subsequent pages
      }
      
      // Draw table header on each page
      y = drawRow(headers, y, true);
      
      // Draw data rows until we run out of space
      while (currentRowIndex < pdfRows.length) {
        const row = pdfRows[currentRowIndex];
        
        // Calculate how much space this row will need
        let rowLines = 1;
        row.forEach((cellContent) => {
          if (cellContent && cellContent.includes('\n')) {
            const lines = cellContent.split('\n').length;
            rowLines = Math.max(rowLines, lines);
          }
        });
        const rowHeight = lineHeight * rowLines;
        
        // Check if there's enough space for this row
        if (y + rowHeight > pageHeight - margins.bottom - lineHeight) {
          break; // Move to next page
        }
        
        y = drawRow(row, y);
        currentRowIndex++;
      }
      
      pageNum++;
    }
    
    // Add user and date at the end
    const {
      user: { profile },
    } = getAppBarState().appState;
    const now = new Date();
    pdf.setFontSize(fontSize);
    pdf.setFont('helvetica', 'normal');
    const userDateText = `${profile?.name || 'Usuario'} ${formatToShortLocalDateTime(now, 'DD-MM-YYYY h:mm:ss a')}`;
    pdf.text(userDateText, pageWidth - margins.right, pageHeight - margins.bottom + 5, { align: 'right' });
    
    pdf.save(`${filename}.pdf`);
  } catch (error) {
    console.error('Error generating PDF:', error);
    alert(`Error generating PDF: ${error.message || 'Unknown error'}`);
  }
}

// Print-specific styles for page size, orientation, and font
if (typeof window !== 'undefined') {
  const style = document.createElement('style');
  style.innerHTML = `
    @media print {
      @page {
        size: letter portrait;
        margin: 0mm;
      }
      body, .MuiPaper-root, .MuiTableCell-root, .MuiTypography-root, .MuiTable-root {
        font-family: Roboto, Arial, sans-serif !important;
        font-size: 12px !important;
      }
      .MuiGrid-item {
        padding-top: 0 !important;
        padding-bottom: 0 !important;
      }
      .MuiStack-root {
        gap: 0 !important;
      }
    }
  `;
  document.head.appendChild(style);
}
